import { Form, useForm } from '@magicdoorinc/form';
import { Button, useLocalization, Modal, IconLock } from '@magicdoorinc/ui';
import { createMemo, createSignal, createEffect, Show } from 'solid-js';
import IconArrow from '~/assets/images/common/lineArrow.svg?component-solid';
import LabeledBankingInputForPayabli from '~/components/common/Inputs/form-inputs/LabeledBankingInputForPayabli';
import { PayabliCardSetupPanel } from '~/components/common/PayabliCardSetupPanel';
import { useCurrentCompany, usePayPaymentMethods } from '~/contexts/global';
import { IconCards } from '~/pages/tenants/tenant-details/components/TenantPaymentDetailsModal';
import type { TrialPaymentMethodModalProps } from './trialPaymentMethodModal.types';
import type { PlaidFormStore } from '~/components/common/PortalBankAccountPaymentGate';
import type { BankAccountType } from '~/swagger/pay/Api';

const SectionHeader = (props: { title: string; subtitle: string; onBack: () => void }) => (
  <div class="flex items-center gap-4">
    <Button variant="outlined" onClick={props.onBack} class="px-2 pb-1" size="sm">
      <IconArrow class="size-4 rotate-90" />
    </Button>
    <div>
      <h3 class="text-lg font-semibold text-gray-900 sm:text-xl">{props.title}</h3>
      <p class="mt-1 text-sm text-gray-600">{props.subtitle}</p>
    </div>
  </div>
);

const PaymentMethodButton = (props: { onClick: () => void; disabled: boolean; title: string; subtitle: string }) => (
  <Button
    onClick={props.onClick}
    class="group flex h-full flex-col items-center justify-center gap-3 rounded-xl border-2 border-gray-200 p-6 transition-all duration-200"
    variant="outlined"
    disabled={props.disabled}>
    <div class="flex size-12 items-center justify-center rounded-lg">
      <IconCards />
    </div>
    <div class="text-center">
      <div class="text-base font-semibold text-text-01 sm:text-base">{props.title}</div>
      <div class="text-xs text-text-02 sm:text-sm">{props.subtitle}</div>
    </div>
  </Button>
);

export const PayTrialPaymentMethodGate = (props: TrialPaymentMethodModalProps) => {
  const { t } = useLocalization();
  const { currentCompany } = useCurrentCompany();
  const { createPaymentMethod } = usePayPaymentMethods();

  const [paymentMethod, setPaymentMethod] = createSignal<'card' | 'ach' | null>(null);
  const [isProcessing, setIsProcessing] = createSignal(false);

  const achForm = useForm();

  const runAddRef: { run: () => void } = { run: () => {} };

  const companyId = createMemo(() => currentCompany()?.id);

  const handleClose = () => {
    setPaymentMethod(null);
    props.onClose();
  };

  const handleBack = () => {
    setPaymentMethod(null);
  };

  const finishSuccess = () => {
    props.onSuccess?.();
    handleClose();
    window.location.reload();
  };

  const handleCardSuccess = () => {
    finishSuccess();
  };

  const handleAchSubmit = async () => {
    const { validateStatus } = achForm.validateForm();
    if (!validateStatus) return;

    const id = companyId();
    if (id == null) return;

    setIsProcessing(true);
    try {
      const formStore = achForm.formStore as PlaidFormStore;
      await createPaymentMethod({
        source: 'ach',
        holderName: formStore.holderName as string,
        routingNumber: formStore.routingNumber as string,
        accountNumber: formStore.accountNumber as string,
        accountType: formStore.accountType as string as BankAccountType,
      });
      finishSuccess();
    } catch {
      setIsProcessing(false);
    }
  };

  createEffect(() => {
    if (props.visible) {
      setPaymentMethod(null);
    }
  });

  return (
    <Modal
      title={t('Add payment method')}
      visible={props.visible}
      onCancel={handleClose}
      showCloseButton={!paymentMethod()}
      showFooter={true}
      footer={
        <div class="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 p-4 sm:flex-row sm:justify-between sm:p-6">
          <Show when={paymentMethod()}>
            <Button variant="outlined" onClick={handleBack} class="hidden w-full justify-center sm:block sm:w-auto">
              {t('Back')}
            </Button>
          </Show>
          <div class="order-1 flex w-full flex-col items-end justify-end gap-3 sm:flex-row">
            <Button variant="outlined" onClick={handleClose} class="w-full justify-center sm:w-auto">
              {t('Cancel')}
            </Button>
            <Show when={paymentMethod() === 'card'}>
              <Button onClick={() => runAddRef.run()} loading={createPaymentMethod.loading} class="w-full justify-center sm:w-auto">
                {t('Add')}
              </Button>
            </Show>
            <Show when={paymentMethod() === 'ach'}>
              <Button onClick={handleAchSubmit} loading={isProcessing()} class="w-full justify-center sm:w-auto">
                {t('Add')}
              </Button>
            </Show>
          </div>
        </div>
      }
      class="flex max-h-[90vh] w-full max-w-4xl flex-col sm:mx-6 lg:mx-8">
      <div class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <Show when={!paymentMethod()}>
          <div class="space-y-6">
            <div class="space-y-2 text-center">
              <h2 class="text-xl font-bold text-gray-900 sm:text-2xl">{t('Add payment method')}</h2>
              <p class="mx-auto max-w-2xl text-sm text-gray-600 sm:text-base">
                {t('Choose a payment method to continue using the application')}
              </p>
            </div>

            <div class="mx-auto grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
              <PaymentMethodButton
                onClick={() => setPaymentMethod('card')}
                disabled={isProcessing()}
                title={t('Credit/Debit Cards')}
                subtitle={t('Add via {provider}', { provider: 'MagicDoor' })}
              />

              <PaymentMethodButton
                onClick={() => setPaymentMethod('ach')}
                disabled={isProcessing()}
                title={t('Bank account')}
                subtitle={t('Add using bank routing and account number')}
              />
            </div>

            <div class="mx-auto max-w-2xl">
              <div class="rounded-lg border p-4">
                <div class="flex items-start gap-3">
                  <IconLock class="size-5" />
                  <div class="text-sm">
                    <p class="mb-1 font-medium capitalize">{t('Secure payment processing')}</p>
                    <p>{t('Your payment information is encrypted and secure. We never store your full card details')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Show>

        <Show when={paymentMethod() === 'card'}>
          <div class="space-y-6">
            <SectionHeader
              title={t('Add credit/debit card')}
              subtitle={t('Complete your payment setup with {provider}', { provider: 'MagicDoor' })}
              onBack={handleBack}
            />
            <PayabliCardSetupPanel
              active={props.visible && paymentMethod() === 'card'}
              onSuccess={handleCardSuccess}
              onRegisterAddAction={(run) => {
                runAddRef.run = run;
              }}
            />
          </div>
        </Show>

        <Show when={paymentMethod() === 'ach'}>
          <div class="space-y-6">
            <SectionHeader
              title={t('Add bank account')}
              subtitle={t('Enter your bank account details to receive payments')}
              onBack={handleBack}
            />

            <div class="rounded-lg border border-gray-200 p-4 sm:p-6">
              <Form defaultForm={achForm} class="space-y-6">
                <LabeledBankingInputForPayabli />
              </Form>
            </div>
          </div>
        </Show>
      </div>
    </Modal>
  );
};
